import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Megaphone, Plus, Pencil, Trash2, Eye, EyeOff, 
  AlertTriangle, AlertCircle, Info, Save, X
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatDateTime as formatDateTimePST } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

const PRIORITY_CONFIG = {
  normal: { label: 'Normal', icon: Info, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  high: { label: 'High', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  urgent: { label: 'Urgent', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' }
};

function Announcements({ user }) {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${API}/announcements/all`, { credentials: 'include' });
      if (response.ok) {
        setAnnouncements(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setFormData({ title: '', content: '', priority: 'normal' });
    setShowModal(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority || 'normal'
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingAnnouncement 
        ? `${API}/announcements/${editingAnnouncement.announcement_id}`
        : `${API}/announcements`;
      
      const response = await fetch(url, {
        method: editingAnnouncement ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingAnnouncement ? 'Announcement updated' : 'Announcement created');
        setShowModal(false);
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save announcement');
      }
    } catch (error) {
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      const response = await fetch(`${API}/announcements/${announcement.announcement_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: !announcement.active })
      });

      if (response.ok) {
        toast.success(announcement.active ? 'Announcement hidden' : 'Announcement shown');
        fetchAnnouncements();
      } else {
        toast.error('Failed to update announcement');
      }
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      const response = await fetch(`${API}/announcements/${deleteConfirm.announcement_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Announcement deleted');
        setDeleteConfirm(null);
        fetchAnnouncements();
      } else {
        toast.error('Failed to delete announcement');
      }
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const formatDate = (dateStr) => {
    return formatDateTimePST(dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Announcements</h1>
              <p className="text-sm text-gray-600">Manage banners shown to clients</p>
            </div>
          </div>
          <Button 
            onClick={openCreateModal}
            className="bg-[#80298F] hover:bg-[#9b3ba8] w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Announcement
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{announcements.length}</p>
              <p className="text-xs sm:text-sm text-gray-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-green-600">{announcements.filter(a => a.active).length}</p>
              <p className="text-xs sm:text-sm text-gray-500">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-gray-400">{announcements.filter(a => !a.active).length}</p>
              <p className="text-xs sm:text-sm text-gray-500">Hidden</p>
            </CardContent>
          </Card>
        </div>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card>
            <CardContent className="py-8 sm:py-12 text-center">
              <Megaphone className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No announcements yet</p>
              <Button onClick={openCreateModal} className="bg-[#80298F] hover:bg-[#9b3ba8]">
                <Plus className="h-4 w-4 mr-2" />
                Create First Announcement
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {announcements.map(announcement => {
              const priority = PRIORITY_CONFIG[announcement.priority] || PRIORITY_CONFIG.normal;
              const PriorityIcon = priority.icon;
              
              return (
                <Card 
                  key={announcement.announcement_id} 
                  className={`${!announcement.active ? 'opacity-60' : ''} ${priority.border} border-l-4 overflow-hidden`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col gap-3">
                      {/* Title and badges row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <PriorityIcon className={`h-4 w-4 ${priority.color} flex-shrink-0`} />
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{announcement.title}</h3>
                        {!announcement.active && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Hidden</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded ${priority.bg} ${priority.color}`}>
                          {priority.label}
                        </span>
                      </div>
                      
                      {/* Content */}
                      <p className="text-gray-600 text-sm line-clamp-2">{announcement.content}</p>
                      
                      {/* Meta info */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        <span className="truncate">By {announcement.created_by_name}</span>
                        <span>•</span>
                        <span>{formatDate(announcement.created_at)}</span>
                      </div>
                      
                      {/* Actions row */}
                      <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(announcement)}
                          title={announcement.active ? 'Hide' : 'Show'}
                          className="h-8 px-2"
                        >
                          {announcement.active ? (
                            <EyeOff className="h-4 w-4 text-gray-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-green-600" />
                          )}
                          <span className="ml-1 text-xs hidden sm:inline">{announcement.active ? 'Hide' : 'Show'}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(announcement)}
                          className="h-8 px-2"
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="ml-1 text-xs hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          onClick={() => setDeleteConfirm(announcement)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="ml-1 text-xs hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Announcement title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Announcement message..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">
                    <span className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-blue-600" />
                      Normal
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      High
                    </span>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      Urgent
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              {saving ? 'Saving...' : (editingAnnouncement ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Delete Announcement</DialogTitle>
          </DialogHeader>
          
          <p className="py-4">
            Are you sure you want to delete "<span className="font-semibold">{deleteConfirm?.title}</span>"? 
            This action cannot be undone.
          </p>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Announcements;
